import type { PaginationMetadata, PaginationQuery } from "./common";
import type { SalePaymentMethod } from "./sale";

export const EXPENSE_CATEGORIES = [
  "raw_material",
  "packaging",
  "rider_bonus",
  "salary",
  "transportation",
  "utilities_operational",
  "equipment",
  "marketing",
  "operational",
  "other",
] as const;

export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export const NON_STOCK_EXPENSE_CATEGORIES: readonly ExpenseCategory[] = [
  "salary",
  "rider_bonus",
];

export type NonStockExpenseCategory = "salary" | "rider_bonus";

export function isNonStockExpenseCategory(
  category: string,
): category is NonStockExpenseCategory {
  return (NON_STOCK_EXPENSE_CATEGORIES as readonly string[]).includes(category);
}

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  raw_material: "Bahan Baku",
  packaging: "Kemasan",
  rider_bonus: "Bonus Rider",
  salary: "Gaji",
  transportation: "Transportasi",
  utilities_operational: "Listrik & Operasional",
  equipment: "Peralatan",
  marketing: "Marketing",
  operational: "Operasional",
  other: "Lain-lain",
};

export const EXPENSE_PAYMENT_METHODS = [
  "cash",
  "qris",
  "bank_mandiri",
  "loan",
] as const;

export type ExpenseFundingSource = (typeof EXPENSE_PAYMENT_METHODS)[number];

export const EXPENSE_STATUSES = ["draft", "done"] as const;

/** draft: editable, books nothing. done: approved, stock booked, locked. */
export type ExpenseStatus = (typeof EXPENSE_STATUSES)[number];
export type ExpenseAccountingTreatment =
  | "inventory_cogs"
  | "rider_bonus"
  | "rider_salary"
  | "operational";
export type ExpenseValidation = "ok" | "check_amount" | "unpaid" | "proof_missing";

import type { StockItemType } from "./stock";

export interface ExpenseItem {
  id: string;
  expenseId: string;
  description: string;
  itemType?: StockItemType;
  itemId?: string;
  uomId?: string;
  qty: number;
  qtyInUom?: number;
  pricePerUnit: number;
  subtotal: number;
  item?: {
    id: string;
    name: string;
    sku: string | null;
    uomName: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateExpenseItemInput {
  description: string;
  itemType?: StockItemType;
  itemId?: string;
  uomId?: string;
  qty?: number;
  qtyInUom?: number;
  pricePerUnit: number;
}

export interface Expense {
  id: string;
  workspaceId: string;
  code: string | null;
  date: string;
  category: ExpenseCategory;
  description: string;
  qty: number;
  pricePerUnit: number;
  amount: number;
  subtotalAmount?: number;
  discountAmount?: number;
  taxAmount?: number;
  items?: ExpenseItem[];
  paymentMethod: SalePaymentMethod;
  fundingSource: ExpenseFundingSource;
  referenceNote: string | null;
  status: ExpenseStatus;
  completedAt?: string | null;
  /** Set when auto-generated by marking a rider payroll paid. */
  payrollId?: string | null;
  /** true when payrollId is set — edit/delete via this module is blocked. */
  locked?: boolean;
  receiptKey?: string | null;
  receiptKeys?: string[];
  receiptUrl?: string | null;
  receiptUrls?: string[];
  accountingTreatment: ExpenseAccountingTreatment;
  countsTowardNetProfit: boolean;
  cashOut: number;
  validation: ExpenseValidation;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExpenseReq {
  /** Defaults to draft on the API; done needs the expenses.approve permission. */
  status?: ExpenseStatus;
  date: string;
  category: ExpenseCategory;
  description?: string;
  qty?: number;
  pricePerUnit?: number;
  items?: CreateExpenseItemInput[];
  discountAmount?: number;
  taxAmount?: number;
  paymentMethod?: SalePaymentMethod;
  fundingSource?: ExpenseFundingSource;
  referenceNote?: string;
  receiptKey?: string | null;
  receiptKeys?: string[];
  acknowledgeCostImpact?: boolean;
  // Only honored for actors with canSwitchWorkspace — everyone else always
  // creates in their own workspace regardless of what's sent here.
  workspaceId?: string;
}

export interface CompleteExpenseReq {
  acknowledgeCostImpact?: boolean;
}

export interface RawMaterialCostImpact {
  rawMaterialId: string;
  rawMaterialName: string;
  currentCost: number;
  newCost: number;
}

export interface ProductCostImpact {
  productId: string;
  productName: string;
  recipeId: string;
  currentCogs: number;
  newCogs: number;
}

export interface ExpenseCostImpactPreview {
  rawMaterials: RawMaterialCostImpact[];
  products: ProductCostImpact[];
  hasImpact: boolean;
}

export interface UpdateExpenseReq {
  id?: string;
  date?: string;
  category: ExpenseCategory;
  description?: string;
  qty?: number;
  pricePerUnit?: number;
  items?: CreateExpenseItemInput[];
  discountAmount?: number;
  taxAmount?: number;
  paymentMethod?: SalePaymentMethod;
  fundingSource?: ExpenseFundingSource;
  referenceNote?: string;
  receiptKey?: string | null;
  receiptKeys?: string[];
}

export interface GetExpenseReq {
  q?: string;
  category?: ExpenseCategory;
  paymentMethod?: SalePaymentMethod;
  dateFrom?: string;
  dateTo?: string;
  pagination?: PaginationQuery;
}

export interface ExpenseList {
  items: Expense[];
  pagination: PaginationMetadata;
}
